import { green, orange } from '@ant-design/colors'
import { ArrowRightOutlined, SignatureOutlined } from '@ant-design/icons'
import { Campaign } from '@components/campaign/Campaign'
import { CampaignEmployee } from '@components/campaignEmployee/CampaignEmployee'
import { Company } from '@components/company/Company'
import { Employee } from '@components/employee/Employee'
import { Form } from '@components/form/Form'
import { Job } from '@components/job/Job'
import {
  CampaignCounts,
  CampaignEmployee as CampaignEmployeeModel,
} from '@models/Campaign'
import { fetchCampaignCountsById } from '@services/CampaignService'
import {
  getCampaignEmployees,
  selectCampaignEmployees,
  setCampaignEmployeeQuery,
} from '@store/CampaignEmployeeReducer'
import { getCampaignWithId, selectCampaignById } from '@store/CampaignReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import {
  SBTable,
  SBTableFilters,
  TableParams,
} from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction } from '@supplement-bacon/sbrm-react'
import { initialQuery } from '@utils/CRUDHelper'
import { Entity } from '@utils/Entities'
import { useAppDispatch, useAppSelector } from '@utils/hooks'
import { extractSortDataFromSorterResult } from '@utils/table/sorter'
import { Flex, Input, Progress, Space, Spin, Tag, Typography } from 'antd'
import { ColumnsType, TableProps } from 'antd/es/table'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

const { Title, Text } = Typography

const CampaignPage = () => {
  const dispatch = useAppDispatch()
  const { campaignId } = useParams<{ campaignId: string }>()

  const [filtering, setFiltering] = useState<boolean>(false)
  const [filteringLoading, setFilteringLoading] = useState<boolean>(false)
  const [filteringCounts, setFilteringCounts] = useState<CampaignCounts>()

  const items = useAppSelector(selectCampaignEmployees())
  const campaign = useAppSelector(selectCampaignById(Number(campaignId)))
  const { query, isLoading } = useAppSelector((state) => state.campaignEmployee)

  const columns: ColumnsType<CampaignEmployeeModel> = [
    {
      key: 'name',
      title: 'Nom',
      width: 350,
      render: (_, record) => <Employee.Avatar id={record.employee} />,
    },
    {
      key: 'employee',
      title: 'Employé',
      render: (_, record) => (
        <Space>
          <Progress
            type="dashboard"
            percent={record.employeeProgression.progression}
            size={35}
            strokeColor={
              record.employeeProgression.progression == 100
                ? green[4]
                : orange[4]
            }
          />
          <Tag
            color={record.employeeSignedAt ? 'green' : 'orange'}
            icon={<SignatureOutlined />}
          >
            {record.employeeSignedAt ? 'Signé' : 'Non signé'}
          </Tag>
        </Space>
      ),
    },
    {
      key: 'manager',
      title: 'Manager',
      render: (_, record) => (
        <Flex>
          {record.manager && (
            <Space>
              <Progress
                type="dashboard"
                percent={record.managerProgression.progression}
                size={35}
                strokeColor={
                  record.managerProgression.progression == 100
                    ? green[4]
                    : orange[4]
                }
              />
              <Tag
                color={record.managerSignedAt ? 'green' : 'orange'}
                icon={<SignatureOutlined />}
              >
                {record.managerSignedAt ? 'Signé' : 'Non signé'}
              </Tag>
              <Employee.Avatar id={record.manager} type="condensed" />
            </Space>
          )}
        </Flex>
      ),
    },
    {
      key: 'form',
      title: 'Formulaire',
      render: (_, record) => <Form.Tag id={record.form} />,
    },
    {
      key: 'positions',
      title: 'Poste',
      render: (_, record) => <Job.Tag id={record.job} />,
    },
    {
      key: 'companies',
      title: 'Société',
      render: (_, record) => <Company.Tag id={record.company} />,
    },
    {
      key: 'actions',
      title: 'Actions',
      align: 'center',
      render: (_, record) => (
        <Space>
          <SBRM.Actions
            actions={[SBRMAction.Update]}
            entity={Entity.campaignEmployee}
            entityId={record.id}
          />
          {record.canDownloadPDF && (
            <CampaignEmployee.Download campaignEmployeeId={record.id} />
          )}
          <CampaignEmployee.SendEmail campaignEmployeeId={record.id} />
        </Space>
      ),
    },
  ]

  const handleTableChange: TableProps<CampaignEmployeeModel>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    const newQuery: TableParams = {
      pagination,
      filters,
      ...extractSortDataFromSorterResult(sorter),
    }
    dispatch(setCampaignEmployeeQuery(newQuery))
    dispatch(getCampaignEmployees(newQuery))
  }

  useEffect(() => {
    if (!campaignId || !Number(campaignId)) return
    dispatch(getCampaignWithId(Number(campaignId)))

    const baseQuery: TableParams = {
      ...initialQuery,
      filters: { campaigns: [Number(campaignId)] },
    }
    dispatch(setCampaignEmployeeQuery(baseQuery))
    dispatch(getCampaignEmployees(baseQuery))
  }, [campaignId])

  if (!campaign) {
    return (
      <Flex>
        <Spin />
      </Flex>
    )
  }

  return (
    <Flex vertical className="container-row">
      <PageTitle
        level={3}
        title={
          <Space align="end">
            <Title style={{ margin: 0 }} level={1}>
              {campaign.name}
            </Title>
            <Text type="secondary" style={{ lineHeight: 2 }}>
              {dayjs(campaign.start).format('DD/MM/YYYY')}{' '}
              <ArrowRightOutlined />{' '}
              {dayjs(campaign.softEnd).format('DD/MM/YYYY')} | Réelle fin le{' '}
              {dayjs(campaign.hardEnd).format('DD/MM/YYYY')}
            </Text>
          </Space>
        }
        toolbar={<Campaign.SendEmail campaign={campaign} />}
      />

      {!filtering && (
        <Flex vertical>
          <PageTitle level={5} title="Progression globale" />
          <Campaign.StatisticRow counts={campaign} />
        </Flex>
      )}

      {filtering && (
        <Flex vertical>
          <PageTitle level={5} title="Progression d'après les filtres" />
          <Campaign.StatisticRow
            loading={filteringLoading}
            counts={filteringCounts}
          />
        </Flex>
      )}

      <PageTitle level={5} title="Employés" />
      <SBTableFilters
        onValuesChange={(filters, search) => {
          const newQuery: TableParams = {
            ...query,
            pagination: { ...query.pagination, current: 1 },
            filters: { ...query.filters, ...filters },
            search,
          }
          dispatch(setCampaignEmployeeQuery(newQuery))
          dispatch(getCampaignEmployees(newQuery))

          const hasActiveFilter = Object.values({
            ...filters,
            ...search,
          }).some(
            (value) =>
              value !== undefined &&
              value !== null &&
              (typeof value === 'string' ? value.trim().length > 0 : true) &&
              !(Array.isArray(value) && value.length === 0)
          )

          setFiltering(hasActiveFilter)

          if (!hasActiveFilter) {
            return
          }

          setFilteringLoading(true)
          setFilteringCounts(undefined)
          fetchCampaignCountsById(campaign.id, {
            pagination: { current: 1, pageSize: 999999 },
            filters,
            search,
          })
            .then((response) => {
              setFilteringCounts(response.data)
            })
            .finally(() => {
              setFilteringLoading(false)
            })
        }}
        left={[
          {
            name: 'employee',
            type: 'search',
            label: 'Nom',
            style: { maxWidth: 250 },
            input: (
              <Input placeholder="Rechercher employee/manager" allowClear />
            ),
          },
          {
            name: 'companies',
            type: 'filter',
            label: 'Sociétés',
            style: { maxWidth: 250 },
            input: (
              <SBAsyncSelect
                multiple
                allowClear
                entity={Entity.company}
                style={{ minWidth: '16ch' }}
                placeholder="Sélectionner des sociétés"
              />
            ),
          },
          {
            name: 'positions',
            type: 'filter',
            label: 'Postes',
            input: (
              <SBAsyncSelect
                multiple
                allowClear
                entity={Entity.job}
                style={{ minWidth: '16ch' }}
                placeholder="Sélectionner des postes"
              />
            ),
          },
        ]}
      />
      <SBTable
        scroll={{ x: 600 }}
        columns={columns}
        dataSource={items}
        pagination={query.pagination}
        loading={isLoading}
        onChange={handleTableChange}
      />
    </Flex>
  )
}

export default CampaignPage
